'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { IS_PA_BUILD } from '@/lib/nav-items'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
}

// ── Default capabilities (shown on Dashboard and unknown pages) ──────────────
const DEFAULT_CAPABILITIES: Capability[] = [
  { icon: Icons.barChart, label: 'System overview', query: 'Give me a system overview — how many PRs, contracts, tenders, and vendors do we have?' },
  { icon: Icons.clipboardList, label: 'Create a purchase request', query: 'I need to create a new purchase request' },
  { icon: Icons.layers, label: 'Show tender pipeline', query: 'Show me all tenders in the pipeline' },
  { icon: Icons.users, label: 'Show vendor list', query: 'Show me all available vendors' },
  { icon: Icons.fileText, label: 'List purchase requests', query: 'Show me all purchase requests' },
  { icon: Icons.sparkles, label: 'Pending approvals', query: 'What is currently waiting for my approval?' },
]

// ── Tender workbench capabilities ────────────────────────────────────────────
const TENDER_WORKBENCH_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'Tender status', query: 'What is the current status of this tender and what is the next step?' },
  { icon: Icons.shield, label: 'Policy compliance', query: 'Run a compliance check on this tender against all PP-01 to PP-08 policies' },
  { icon: Icons.fileText, label: 'Business case', query: 'Summarise the business case for this tender' },
  { icon: Icons.layers, label: 'Evaluation results', query: 'Show me the bid evaluation results for this tender' },
  { icon: Icons.sparkles, label: 'Next gate action', query: 'What gate decision is required for this tender and what evidence is needed?' },
  { icon: Icons.clipboardList, label: 'Audit trail', query: 'Show me the audit trail and key decisions made for this tender' },
]

// ── Tender pipeline capabilities ─────────────────────────────────────────────
const TENDER_PIPELINE_CAPABILITIES: Capability[] = [
  { icon: Icons.layers, label: 'Show all tenders', query: 'Show me all tenders in the pipeline grouped by stage' },
  { icon: Icons.barChart, label: 'Tenders at gate', query: 'Which tenders are currently awaiting gate approval?' },
  { icon: Icons.plus, label: 'Create a tender', query: 'I want to create a new public sector tender' },
  { icon: Icons.search, label: 'Find a tender', query: 'Help me find a specific tender' },
  { icon: Icons.shield, label: 'Policy library', query: 'What does PP-03 say about abnormally low bids?' },
  { icon: Icons.sparkles, label: 'Procurement Act', query: 'What are the key obligations under the Procurement Act 2023?' },
]

// ── Procurement (AP) capabilities ────────────────────────────────────────────
const PROCUREMENT_CAPABILITIES: Capability[] = [
  { icon: Icons.clipboardList, label: 'Create a purchase request', query: 'I need to create a new purchase request' },
  { icon: Icons.layers, label: 'List purchase requests', query: 'Show me all purchase requests' },
  { icon: Icons.sparkles, label: 'Pending approvals', query: 'What is currently waiting for my approval?' },
  { icon: Icons.barChart, label: 'Check department budget', query: 'Check budget availability for Engineering department' },
  { icon: Icons.users, label: 'Recommend vendors', query: 'I need vendor recommendations for IT equipment' },
  { icon: Icons.search, label: 'Pipeline status', query: 'Show me the overall procurement pipeline status' },
]

// ── PR detail capabilities ────────────────────────────────────────────────────
const PR_DETAIL_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'PR status', query: 'What is the current status of this PR and who needs to approve it?' },
  { icon: Icons.users, label: 'Recommend vendors', query: 'Recommend vendors for the item in this PR' },
  { icon: Icons.layers, label: 'Create RFQ', query: 'Create an RFQ for this purchase request' },
  { icon: Icons.sparkles, label: 'Approval workflow', query: 'Show me the approval workflow for this PR' },
  { icon: Icons.barChart, label: 'Check budget', query: 'Check if the budget for this PR is available in the department' },
  { icon: Icons.clipboardList, label: 'Amend PR', query: 'I need to amend this purchase request' },
]

// ── Vendor detail capabilities ────────────────────────────────────────────────
const VENDOR_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'Vendor performance', query: 'What is the performance history of this vendor?' },
  { icon: Icons.shield, label: 'Due diligence', query: 'Run a due diligence report on this vendor' },
  { icon: Icons.layers, label: 'Transaction history', query: 'Show me all purchase orders and transactions with this vendor' },
  { icon: Icons.clipboardList, label: 'Onboarding status', query: 'What is the current onboarding status of this vendor?' },
  { icon: Icons.sparkles, label: 'Compare vendors', query: 'Compare this vendor with other vendors in the same category' },
  { icon: Icons.barChart, label: 'Active contracts', query: 'Show me all active contracts with this vendor' },
]

// ── Contract hub capabilities ─────────────────────────────────────────────────
const CONTRACT_CAPABILITIES: Capability[] = [
  { icon: Icons.layers, label: 'List contracts', query: 'Show me all active contracts' },
  { icon: Icons.barChart, label: 'Contract stats', query: 'Show me contract KPIs — total value, expiring soon, by vendor' },
  { icon: Icons.search, label: 'Expiring soon', query: 'Which contracts are expiring in the next 90 days?' },
  { icon: Icons.fileText, label: 'Contract detail', query: 'Show me the details of this contract' },
  { icon: Icons.sparkles, label: 'Renewal risk', query: 'Which contracts have renewal risk or KPI breaches?' },
  { icon: Icons.clipboardList, label: 'Invoice match', query: 'Show me invoices pending 3-way match for this contract' },
]

// ── Invoice capabilities ──────────────────────────────────────────────────────
const INVOICE_CAPABILITIES: Capability[] = [
  { icon: Icons.layers, label: 'Pending invoices', query: 'Show me all invoices pending review or approval' },
  { icon: Icons.search, label: 'Check duplicate', query: 'Check if this invoice is a duplicate' },
  { icon: Icons.shield, label: '3-way match', query: 'Run a 3-way match check on pending invoices' },
  { icon: Icons.barChart, label: 'Invoice overview', query: 'Give me an overview of invoice status — pending, approved, overdue' },
  { icon: Icons.sparkles, label: 'Overdue invoices', query: 'Show me all overdue invoices that need attention' },
  { icon: Icons.clipboardList, label: 'Find invoice', query: 'Find a specific invoice by vendor or PO number' },
]

// ── Analytics/Dashboard capabilities ─────────────────────────────────────────
const ANALYTICS_CAPABILITIES: Capability[] = [
  { icon: Icons.barChart, label: 'Spend by department', query: 'Show me spend breakdown by department year-to-date' },
  { icon: Icons.layers, label: 'Top vendors by spend', query: 'Who are our top 10 vendors by spend this year?' },
  { icon: Icons.search, label: 'Cycle time analysis', query: 'What is the average PR-to-PO cycle time?' },
  { icon: Icons.sparkles, label: 'Generate report', query: 'Generate a procurement spend report for this quarter' },
  { icon: Icons.fileText, label: 'Category analysis', query: 'Show me spend by category and identify any anomalies' },
  { icon: Icons.shield, label: 'Compliance summary', query: 'Give me a compliance summary across all procurement activities' },
]

// ── AI policies capabilities ──────────────────────────────────────────────────
const AI_POLICIES_CAPABILITIES: Capability[] = [
  { icon: Icons.shield, label: 'Policy overview', query: 'List all active AI policies and what they enforce' },
  { icon: Icons.search, label: 'PP-03 (Abnormal bids)', query: 'What does PP-03 say about abnormally low bid detection?' },
  { icon: Icons.layers, label: 'PP-07 (MAT/MEAT)', query: 'Explain the MAT/MEAT methodology under PP-07' },
  { icon: Icons.sparkles, label: 'Procurement Act 2023', query: 'What are the key obligations under the Procurement Act 2023?' },
  { icon: Icons.fileText, label: 'Knowledge Hub search', query: 'Search the Knowledge Hub for information about social value requirements' },
  { icon: Icons.clipboardList, label: 'Policy compliance', query: 'How does this system enforce procurement policy compliance?' },
]

// ── The PA demo (plan 04 section 8.4, plan 02): the briefing's three chips and
// the two rehearsed questions, every one of which has a scripted answer. ─────
export const DEMO_CAPABILITIES: Capability[] = [
  { icon: Icons.sparkles, label: 'What needs me?', query: 'What needs me?' },
  { icon: Icons.layers, label: 'Show me the pipeline', query: 'Show me the pipeline' },
  { icon: Icons.barChart, label: 'How are we doing today?', query: 'How are we doing today?' },
  {
    icon: Icons.search,
    label: 'Which bidder offers the best value?',
    query: 'Which bidder offers the best value, and what should we clarify before award?',
  },
  { icon: Icons.clipboardList, label: 'Where are my pulse oximeters?', query: 'Where are my pulse oximeters?' },
]

function getCapabilitiesForPage(pathname: string): Capability[] {
  if (IS_PA_BUILD) return DEMO_CAPABILITIES
  if (!pathname) return DEFAULT_CAPABILITIES

  // Tender workbench (specific tender) — most specific, check first
  if (/^\/tender\/[A-Z0-9\-]+/.test(pathname)) return TENDER_WORKBENCH_CAPABILITIES

  // Tender pipeline
  if (pathname === '/tender' || pathname.startsWith('/tender?')) return TENDER_PIPELINE_CAPABILITIES

  // PR detail
  if (/^\/procurement\/requests\//.test(pathname)) return PR_DETAIL_CAPABILITIES

  // Procurement AP general
  if (pathname.startsWith('/procurement')) return PROCUREMENT_CAPABILITIES

  // Vendor detail
  if (/^\/vendors\//.test(pathname)) return VENDOR_CAPABILITIES

  // Contract detail or hub
  if (pathname.startsWith('/contracts')) return CONTRACT_CAPABILITIES

  // Invoices
  if (pathname.startsWith('/invoices')) return INVOICE_CAPABILITIES

  // Analytics/reports/dashboard
  if (pathname === '/' || pathname.startsWith('/reports')) return ANALYTICS_CAPABILITIES

  // AI Policies & Knowledge Hub
  if (pathname.startsWith('/ai/') || pathname.startsWith('/knowledge')) return AI_POLICIES_CAPABILITIES

  return DEFAULT_CAPABILITIES
}

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
  currentPage?: string
}

export function CapabilityBubbles({ onSelect, currentPage = '/' }: CapabilityBubblesProps) {
  const capabilities = getCapabilitiesForPage(currentPage)

  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
